import {
  startTransition,
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface UseResultsBarProps {
  totalResults: number;
  setCurrentTab: Dispatch<SetStateAction<number>>;
}

interface UseResultsBarReturn {
  scrollContainerRef: RefObject<HTMLDivElement>;
  showLeftChevron: boolean;
  showRightChevron: boolean;
  handleScroll: () => void;
  scroll: (direction: "left" | "right") => void;
  handleButtonClick: (index: number) => void;
}

export const useResultsBar = ({ totalResults, setCurrentTab }: UseResultsBarProps): UseResultsBarReturn => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(true);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    setShowLeftChevron(container.scrollLeft > 0);
    const isAtMaxScroll = container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;
    setShowRightChevron(!isAtMaxScroll);
  }, []);

  const scroll = useCallback((direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "auto",
    });
  }, []);

  const handleButtonClick = useCallback(
    (index: number) => {
      const container = scrollContainerRef.current;

      if (container) {
        const children = container.children;
        if (index >= 0 && index < children.length) {
          const targetElement = children[index] as HTMLElement;

          targetElement.scrollIntoView({
            behavior: "auto",
            block: "nearest",
            inline: "center",
          });
        }
      }

      startTransition(() => {
        setCurrentTab(index);
      });
    },
    [setCurrentTab]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      return;
    }

    setShowLeftChevron(false);
    setShowRightChevron(totalResults > 2);
  }, [handleScroll, totalResults]);

  return {
    scrollContainerRef,
    showLeftChevron,
    showRightChevron,
    handleScroll,
    scroll,
    handleButtonClick,
  };
};
